import { useEffect, useMemo, useState } from 'react'
import type { CorreoModeloDTO, WrapperResponse } from '../../../model/kartera-api-model'
import { useHistory } from '../../../router/history'
import APIInvoke from '../../../utils/APIInvoke'
import {
    EMAIL_TEMPLATES_PATH,
    emailTemplatePath,
    errorMessage,
    eventTone,
    NEW_EMAIL_TEMPLATE_PATH,
    pageWindow,
} from '../common/email-template-common'

export const PAGE_SIZES = [20, 50, 100]
const LOAD_ERROR = 'No se pudo cargar el listado de modelos de correo.'

export interface EmailTemplateRow {
    id: number
    name: string
    subject: string
    event: string
    eventTone: string
    cc: string
    bcc: string
}

const toRow = (modelo: CorreoModeloDTO): EmailTemplateRow => ({
    id: modelo.id,
    name: String(modelo.nombre || '').trim(),
    subject: String(modelo.asunto || '').trim(),
    event: modelo.evento?.nombre || 'Sin evento',
    eventTone: eventTone(modelo.evento?.tipo),
    cc: String(modelo.cuentaCC || '').trim(),
    bcc: String(modelo.cuentaCCB || '').trim(),
})

const normalize = (value: string) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()

export const useEmailTemplateListModel = () => {
    const history = useHistory()
    const [search, setSearch] = useState('')
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(PAGE_SIZES[0])
    const [reloadToken, setReloadToken] = useState(0)
    const [loading, setLoading] = useState(true)
    const [initialLoadDone, setInitialLoadDone] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [items, setItems] = useState<CorreoModeloDTO[]>([])

    useEffect(() => {
        let active = true
        const load = async () => {
            try {
                setLoading(true)
                setError(null)
                const payload: WrapperResponse<CorreoModeloDTO[]> | null = await (await APIInvoke.invokeGET('/correo-modelo')).json()
                if (!active) return
                if (!payload?.ok) throw new Error(payload?.message || LOAD_ERROR)
                setItems(payload.body || [])
                setInitialLoadDone(true)
            } catch (loadError) {
                if (active) setError(errorMessage(loadError, LOAD_ERROR))
            } finally {
                if (active) setLoading(false)
            }
        }
        load()
        return () => { active = false }
    }, [reloadToken])

    const filtered = useMemo(() => {
        const query = normalize(search.trim())
        const rows = items.map(toRow).sort((left, right) => left.name.localeCompare(right.name, 'es'))
        if (!query) return rows
        return rows.filter((row) => normalize(`${row.name} ${row.subject} ${row.event}`).includes(query))
    }, [items, search])

    const totalItems = filtered.length
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize))
    const currentPage = Math.min(page, totalPages)
    const rangeFrom = totalItems === 0 ? 0 : ((currentPage - 1) * pageSize) + 1
    const rows = filtered.slice(rangeFrom ? rangeFrom - 1 : 0, (rangeFrom ? rangeFrom - 1 : 0) + pageSize)

    return {
        search,
        page: currentPage,
        pageSize,
        loading,
        initialLoadDone,
        error,
        rows,
        totalItems,
        totalPages,
        pages: totalItems > 0 ? pageWindow(currentPage, totalPages) : [],
        rangeFrom,
        rangeTo: totalItems === 0 ? 0 : rangeFrom + rows.length - 1,
        newLocation: { pathname: NEW_EMAIL_TEMPLATE_PATH, state: { backUrl: EMAIL_TEMPLATES_PATH } },
        changeSearch: (value: string) => {
            setSearch(value)
            setPage(1)
        },
        clearSearch: () => {
            setSearch('')
            setPage(1)
        },
        setPage,
        changePageSize: (value: number) => {
            setPageSize(Number(value))
            setPage(1)
        },
        reload: () => setReloadToken((token) => token + 1),
        openTemplate: (id: number) => history.push({ pathname: emailTemplatePath(id), state: { backUrl: EMAIL_TEMPLATES_PATH } }),
    }
}
